import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import AppBar from '../components/AppBar'
import { MAX_PHOTOS } from '../constants/layout'
import { isIOS } from '../utils/platform'

/**
 * 사진 가져오는 방법 — '기록할 사진' 화면의 안내.
 *
 * 왜 따로 한 장을 두나
 * -------------------
 * 사진을 넘기는 길이 두 가지인데 **기기마다 되는 것이 다르다.** 그 사정을
 * 담기 화면에 다 적으면 단추보다 설명이 길어진다. 그렇다고 안 적으면
 * "왜 어떤 사진만 가게가 뜨지?"를 영영 알 수 없다. 그래서 한 줄만 남기고
 * 나머지는 여기로 뺐다.
 *
 * 안드로이드에서 앱 안 담기로 좌표가 오지 않는 것은 우리가 안 만든 게 아니라
 * 운영체제가 막는 것이다(사진 선택기가 좌표를 0 으로 덮는다). 그 사실을
 * 숨기지 않되, 사용자가 읽을 말로 적는다 — 권한 이름 같은 건 쓰지 않는다.
 */

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="mb-24px">
    {/*
      꼭지 글씨는 앱의 다른 꼭지와 같은 갈색(brown-80%-main)이다.
      상단 바 제목·설정 화면의 묶음 이름이 모두 이 색이라, 여기만 먹색이면
      이 페이지만 다른 앱에서 온 것처럼 보인다.
    */}
    <h2 className="text-style-labelL text-brown-80%-main mb-10px">{title}</h2>
    <div className="flex flex-col gap-8px">{children}</div>
  </section>
)

/**
 * '알아두면 좋아요' 한 줄.
 *
 * 머리표(*)를 글자 **바깥**에 둔다. 글 안에 넣으면 줄이 넘어갈 때 둘째 줄이
 * 머리표 아래로 파고들어 왼쪽 선이 어긋난다.
 */
const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex gap-6px">
    {/*
      머리표는 **굵은 가운뎃점**이다. 별표(*)는 글자 윗선에 붙어 떠 보이고,
      '각주'처럼 읽혀 본문보다 덜 중요한 말 같았다. 가운뎃점은 글 높이
      한가운데에 놓여 줄과 나란히 간다.

      **글자가 아니라 CSS 로 그린 점이다.** 가운뎃점 글리프는 굵기를 거의
      따르지 않는다 — 48px 에서 weight 400 과 700 의 잉크가 774 대 776px 로
      사실상 같았다(실측). 점을 굵게 한다는 건 결국 크게 한다는 뜻이라,
      지름을 직접 정하는 편이 확실하다.

      한 가지 더 — `text-style-* 와 font-700 을 같이 쓰면 굵기가 안 먹는다.`
      text-style-* 가 @layer utilities 안에 있어 빌드된 CSS 에서 font-700
      보다 뒤에 오고, 같은 한 겹 선택자끼리는 뒤가 이긴다.

      mt-9px 는 본문 첫 줄(14px · leading-relaxed = 22.75px)의 한가운데에
      점을 놓는 값이다.
    */}
    <span className="shrink-0 w-5px h-5px mt-9px rounded-full bg-textColorText2" />
    <p className="flex-1 text-style-body2 text-textColorText2 leading-relaxed">
      {children}
    </p>
  </div>
)

/**
 * 공유 시트 그림.
 *
 * 스크린샷 대신 **그려서** 쓴다. 남의 앱 화면(사진·연락처 이름)이 찍히지 않고,
 * 기기·OS 버전마다 달라지는 생김새를 따라다니며 다시 찍을 일도 없다.
 *
 * 아이폰 공유 시트에는 TASTEE 로 가는 길이 **둘**이다 — 위의 앱 아이콘 줄과
 * 아래의 동작 줄('TASTEE에 담기'). 둘 다 그려야 어느 쪽을 눌러도 맞다는 것이
 * 보인다. 글로 적으면 "아이콘이 안 보이는데요"가 남는다.
 *
 * 크기는 받은 시안을 재서 맞췄다(2026-10-17). 시안은 2배 이미지라 전부 절반:
 * 칸 57, 칸 사이 22, 가로줄은 가장자리에서 26, 알약은 14 들어온다.
 * **알약이 가로줄보다 더 바깥까지 나간다** — 실제 공유 시트가 그렇다.
 *
 * 첫 칸은 **에어드롭**이다. 처음엔 '미리 알림'을 그렸는데, 그건 사람마다
 * 다른 자리에 있거나 아예 없다. 에어드롭은 공유 시트 맨 앞에 늘 있어서
 * "내 화면에는 저게 없는데요"가 안 나온다. 칸 셋은 가운데로 모았다.
 */
const TILE = 'w-57px h-57px rounded-14px shadow-sm flex items-center justify-center'

/**
 * 에어드롭 아이콘.
 *
 * 받은 아이콘 그림을 재서 그렸다 — 칸 폭을 1 로 봤을 때 바깥 고리는 반지름
 * 0.41, 가운데 고리는 0.238, 가운데 공은 0.095 이고, 아래쪽 60도가 쐐기로
 * 잘려 있다. 흰색을 **55% / 78% / 100%** 세 단계로 겹쳐 그 입체감을 낸다.
 *
 * 왜 그림 파일이 아니라 코드인가 — 남의 앱 아이콘 파일을 우리 서버에 올려
 * 배포하지 않기 위해서다. 선 하나로 된 도형이라 그릴 수 있다.
 */
const WEDGE = 30 // 아래 쐐기의 반각(도)

const ring = (ro: number, ri: number) => {
  const c = 28.5
  const rad = (WEDGE * Math.PI) / 180
  const sx = Math.sin(rad)
  const cy2 = Math.cos(rad)
  const p = (r: number, dir: number) => `${c + dir * r * sx},${c + r * cy2}`
  return (
    `M ${p(ro, -1)} A ${ro},${ro} 0 1 1 ${p(ro, 1)}` +
    ` L ${p(ri, 1)} A ${ri},${ri} 0 1 0 ${p(ri, -1)} Z`
  )
}

const AirDropGlyph: React.FC = () => (
  <svg viewBox="0 0 57 57" className="w-57px h-57px" aria-hidden="true">
    <path d={ring(23.4, 14.6)} fill="#FFFFFF" fillOpacity={0.55} />
    <path d={ring(13.4, 6.2)} fill="#FFFFFF" fillOpacity={0.78} />
    <circle cx={28.5} cy={28.5} r={5.4} fill="#FFFFFF" />
  </svg>
)

/**
 * 삼성 아이콘 모양(초타원).
 *
 * 갤럭시 아이콘은 둥근 네모가 아니라 **초타원**이다. 실제 공유 화면을 재서
 * |x|^n + |y|^n = 1 의 n 을 구하니 2.6 이었다(135px 기준 오차 1px 이내).
 * 둥근 네모(rounded-14px)로 대신하면 한눈에 다른 기기 화면처럼 보인다.
 */
const SQUIRCLE = (() => {
  const N = 2.6
  const A = 67.5
  const pts: string[] = []
  for (let i = 0; i < 72; i += 1) {
    const t = (2 * Math.PI * i) / 72
    const ct = Math.cos(t)
    const st = Math.sin(t)
    const x = A + A * Math.sign(ct) * Math.abs(ct) ** (2 / N)
    const y = A - A * Math.sign(st) * Math.abs(st) ** (2 / N)
    pts.push(`${x.toFixed(2)},${y.toFixed(2)}`)
  }
  return `M ${pts.join(' L ')} Z`
})()

/** 삼성 아이콘 한 칸 — 초타원 바탕 위에 글리프를 얹는다. */
const SamsungTile: React.FC<{ fill: string; children?: React.ReactNode }> = ({
  fill,
  children,
}) => (
  <div className="relative w-57px h-57px">
    <svg viewBox="0 0 135 135" className="w-57px h-57px" aria-hidden="true">
      <path d={SQUIRCLE} fill={fill} />
      {children}
    </svg>
  </div>
)

/**
 * 갤럭시 공유 화면 그림.
 *
 * 아이폰 것과 달리 **동작 줄(알약)이 없다.** 삼성 공유 화면은 앱 아이콘 줄
 * 아래가 목록이라, 없는 것을 그리면 거짓말이 된다. 그래서 카드도 더 낮다.
 */
const GalaxyFigure: React.FC = () => (
  <figure className="mt-12px">
    <div className="w-full rounded-12px bg-white pt-20px pb-19px flex justify-center gap-24px">
      <Tile label="퀵 셰어">
        {/*
          퀵 셰어 — 고리 두 개와 가운데에서 마주보는 화살표.

          받은 아이콘(512px)을 재서 맞췄다. 칸을 135 로 봤을 때 고리 반지름
          41, 선 굵기 6.4, 줄기는 가운데에서 10.6 떨어져 있다.

          **화살촉은 채운 삼각형이 아니라 선으로 그린 꺾쇠(V)다.** 처음엔
          삼각형으로 그렸는데, 그러면 줄기보다 뭉툭해서 다른 아이콘처럼
          보인다. 굵기·둥근 끝을 고리와 같게 두는 것이 핵심이다.
        */}
        <SamsungTile fill="#3A69F4">
          <g
            stroke="#FFFFFF"
            strokeWidth={6.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          >
            {/* 위: 오른쪽 끝 → 위를 지나 왼쪽 끝 → 가운데로 들어오는 줄기 */}
            <path d="M107.1,56.9 A41,41 0 0,0 27.9,56.9 L69,56.9" />
            <path d="M57.5,46.3 L69,56.9 L57.5,67.5" />
            {/* 아래: 같은 모양을 180도 돌린 것 */}
            <path d="M27.9,78.1 A41,41 0 0,0 107.1,78.1 L66,78.1" />
            <path d="M77.5,88.7 L66,78.1 L77.5,67.5" />
          </g>
        </SamsungTile>
      </Tile>

      <Tile label="TASTEE">
        <SamsungTile fill="#FAF5EF">
          <text
            x={67.5}
            y={67.5}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={28}
            fontWeight={700}
            letterSpacing={0.6}
            fill="rgba(75,54,33,0.8)"
          >
            TASTEE
          </text>
        </SamsungTile>
      </Tile>

      <Tile label="더보기">
        <SamsungTile fill="#DEDEE0">
          <circle cx={49.2} cy={67.5} r={4.4} fill="#4D4C50" />
          <circle cx={67.5} cy={67.5} r={4.4} fill="#4D4C50" />
          <circle cx={85.8} cy={67.5} r={4.4} fill="#4D4C50" />
        </SamsungTile>
      </Tile>
    </div>
  </figure>
)

const Tile: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex flex-col items-center w-57px">
    {children}
    {/*
      칸 밑의 이름은 **검정**이다. 앱 글자색(textColorText1)을 쓰면 갈색이 돌아
      공유 시트가 아니라 우리 화면처럼 보인다 — 이 그림만은 남의 화면이어야 한다.
    */}
    <span className="mt-7px text-12px text-black whitespace-nowrap">{label}</span>
  </div>
)

const ShareSheetFigure: React.FC = () => (
  <figure className="mt-12px">
    {/* 위 30 / 아래 23 — 시안 실측(카드 위→칸 31, 알약 아래→카드 끝 23) */}
    <div className="w-full rounded-12px bg-white px-14px pt-30px pb-23px">
      {/* 칸 줄과 가로줄만 12 더 들어온다(= 가장자리에서 26) */}
      <div className="px-12px">
        {/* 칸 셋을 가운데로 모은다 */}
        {/*
          왼쪽으로 붙인다. 실제 아이폰 공유 시트의 앱 줄이 왼쪽부터 차고
          옆으로 밀리기 때문이다 — 시안도 그렇게 잡혀 있다.
        */}
        <div className="flex items-start gap-22px">
          <Tile label="AirDrop">
            <div className={`${TILE} bg-[#007AFF]`}>
              <AirDropGlyph />
            </div>
          </Tile>

          <Tile label="TASTEE">
            <div className={`${TILE} bg-[#FAF5EF]`}>
              <span className="text-12px font-700 tracking-[0.02em] text-brown-80%-main">
                TASTEE
              </span>
            </div>
          </Tile>

          <Tile label="더 보기">
            {/*
              흰 칸에는 **가느다란 테두리**가 있어야 한다. 실제 아이폰
              공유 시트의 '더 보기'가 그렇고, 여기서는 더 그렇다 — 흰 카드
              위에 흰 칸을 올리는 자리라 그림자만으로는 가장자리가 뭉개져
              옆의 에어드롭·TASTEE 칸과 다른 물건처럼 보인다.
            */}
            <div className={`${TILE} bg-white border border-[rgba(0,0,0,0.08)]`}>
              {/*
                점은 **작고 사이가 넓다.** 처음엔 8 짜리 점을 3 간격으로
                붙여 놨는데, 실제 아이폰 화면보다 굵고 뭉쳐 보였다.
                시뮬레이터에 8/3 · 6/6 · 5/6 을 나란히 띄워 보고 **5 / 6**
                으로 정했다 — 점 사이가 지름보다 넓어야 셋이 또렷하게 떨어져
                보인다.
              */}
              <span className="flex gap-6px">
                {[0, 1, 2].map((i) => (
                  <span key={i} className="w-5px h-5px rounded-full bg-[#686870]" />
                ))}
              </span>
            </div>
          </Tile>
        </div>

        <div className="h-1px bg-[#E8E8E8] mt-13px mb-17px" />
      </div>

      <div className="flex items-center gap-9px h-40px pl-16px rounded-full bg-[#ECECEC]">
        <span className="text-8px font-700 tracking-[0.06em] text-[#2E2E2E]">
          TASTEE
        </span>
        <span className="text-16px text-black">TASTEE에 담기</span>
      </div>
    </div>
  </figure>
)

const Step: React.FC<{ n: number; children: React.ReactNode }> = ({
  n,
  children,
}) => (
  <div className="flex gap-10px items-start">
    <span className="shrink-0 mt-2px w-20px h-20px rounded-full bg-4B3621-80% text-white text-12px font-700 flex items-center justify-center tabular-nums">
      {n}
    </span>
    <p className="text-style-body2 text-textColorText2 leading-relaxed flex-1">
      {children}
    </p>
  </div>
)

const CollectGuide: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()

  /*
   * 푸시로 바로 들어온 경우 홈으로 보낸다.
   *
   * 2026-10-18 '앱 업데이트 안내' 푸시의 목적지를 이 화면으로 잡은 것이
   * 잘못이었다. 공지를 눌렀는데 사용법 설명이 뜨는 건 어긋나고, 들어온 뒤
   * 돌아갈 앞 화면도 없다. 이미 나간 푸시는 주소가 박혀 있어 못 바꾸므로
   * 받는 쪽에서 돌려보낸다.
   *
   * from 표식은 그대로 달고 간다 — 열람 집계(usePushOpenReport)가 그 값을
   * 보고 세기 때문이다. 빼면 누가 눌렀는지 알 수 없어진다.
   */
  const campaign = new URLSearchParams(location.search).get('from')
  React.useEffect(() => {
    if (campaign) {
      navigate(`/main-home?from=${encodeURIComponent(campaign)}`, { replace: true })
    }
  }, [campaign, navigate])

  /*
   * 뒤로가기는 앞 화면이 있을 때만 navigate(-1) 이다.
   *
   * 딥링크로 바로 열면 라우터에 앞 기록이 없어서 -1 이 아무 일도 안 하거나
   * 앱 밖으로 나간다. react-router 는 그렇게 들어온 위치의 key 를
   * 'default' 로 둔다 — 그걸로 가린다.
   */
  const goBack = () =>
    location.key === 'default' ? navigate('/main-home', { replace: true }) : navigate(-1)
  /*
    공유하는 **순서는 두 기기가 같고 화면만 다르다.** 그래서 글은 하나로 두고
    그림만 바꾼다.

    OS 이름은 쓰지 않는다. 사용자는 자기 폰을 '아이폰', '갤럭시'라고 부르지
    'iOS 기기'라고 하지 않는다. 그리고 **쓰는 기기 것만 보여주므로** 화면에는
    기기 이름이 나올 일도 없다 — 둘 다 띄우면 절반은 자기와 상관없는 설명을
    읽으며 "내 건 어느 쪽이지"를 한 번 더 판단해야 한다.
  */
  const ios = isIOS()

  /*
   * 사진이 들어 있는 앱은 두 기기 모두 **'앨범'**이라고 부른다.
   *
   * 기기마다 이름이 다르긴 하다 — 갤럭시는 앱 이름이 '갤러리', 아이폰은
   * '사진'(갤러리라는 앱이 아예 없다). 그래도 한 말로 통일했다. 둘 다
   * 앱 안의 사진 묶음을 앨범이라 부르고, 못 알아들을 사람이 없다.
   */

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <AppBar title="사진 가져오는 방법" onBack={goBack} isBottomBorder />
      <div className="app-container flex-1 min-h-0 overflow-y-auto px-20px pt-24px pb-40px">
        <Section title="앨범에서 공유하기">
          <div className="flex flex-col gap-10px mt-2px mb-4px">
            <Step n={1}>앨범에서 기록할 사진들을 선택</Step>
            <Step n={2}>공유 버튼 → TASTEE 아이콘 클릭</Step>
            <Step n={3}>담긴 사진을 선택하고 기록</Step>
          </div>
          {/*
            기기마다 공유 화면이 다르므로 **쓰는 기기 것만** 그린다. 둘 다
            띄우면 절반은 자기와 상관없는 그림을 보며 "내 건 어느 쪽이지"를
            한 번 더 판단해야 한다. 그림 밑 설명은 넣지 않는다 — 윗글의
            2번이 이미 같은 말을 한다.
          */}
          {ios ? <ShareSheetFigure /> : <GalaxyFigure />}
        </Section>

        <Section title="알아두면 좋아요">
          {/* 줄 사이 20, 제목과는 24 — 시안 실측 */}
          <div className="mt-4px flex flex-col gap-10px">
          {/*
            차례는 시안을 그대로 따른다 — 가장 새로 생긴 기능(길게 눌러
            지우기)이 맨 위, 숫자 둘이 붙어 있고, 못 찾는 경우가 그 다음,
            기기에만 해당하는 말이 맨 끝이다.
          */}
          {/*
            길게 누르기는 화면에 흔적이 없다. 이 줄이 없으면 지우는 길을
            **아무도 못 찾는다** — 기능과 한 묶음이라 지우지 말 것.
          */}
          <Note>사진을 길게 누르면 지울 수 있어요</Note>
          {/*
            숫자는 적지 않고 실제 상한을 그대로 가져온다(MAX_PHOTOS).
            한도를 고치면 이 줄도 같이 따라온다. 담는 한도(100)와 기록
            한도(10)는 서로 다른 숫자라 붙여 둔다.
          */}
          <Note>기록에는 최대 {MAX_PHOTOS}장까지 추가할 수 있어요</Note>
          <Note>앨범에서 한 번에 100장까지 공유할 수 있어요</Note>
          <Note>목록에 TASTEE가 안 보이면 '더 보기'를 눌러보세요</Note>
          {/*
            갤럭시만 해당한다. 안드로이드 사진 선택기는 좌표를 0 으로 덮어
            돌려준다(2026-10-13 실측). 아이폰은 앱에서 바로 담아도 좌표가
            그대로 와서, 이 줄을 띄우면 거짓말이 된다.
          */}
          {!ios && (
            <Note>갤러리에서 공유해야 자동으로 위치를 찾아 줄 수 있어요</Note>
          )}
          </div>
        </Section>
      </div>
    </div>
  )
}

export default CollectGuide
